"use client";

import dynamic from "next/dynamic";
import { useMemo } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";

// Dynamically import ProductFruits to avoid SSR issues
// This is required because ProductFruits is a client-only component
const ProductFruits = dynamic(
  () => import("react-product-fruits").then((mod) => mod.ProductFruits),
  { ssr: false }
);

interface ProductFruitsProviderProps {
  children?: React.ReactNode;
}

/**
 * ProductFruits Provider Component
 *
 * Integrates ProductFruits for:
 * - Product tours and onboarding
 * - Feature announcements
 * - In-app hints and tooltips
 * - User feedback collection
 * - Knowledge base / help center
 *
 * User Data Sent to ProductFruits:
 * - username: User's unique ID (required for tracking)
 * - email: User's email address
 * - firstname/lastname: Parsed from user's full name
 * - role: User type (agent, admin, agency_owner, trial, viewer)
 * - signUpAt: Account creation date
 * - props: Custom properties for segmentation:
 *   - userType: agent, admin, agency_owner, trial, viewer
 *   - accountType: trial, basic, full_fledged, enterprise
 *   - accountStatus: active, trial_active, trial_expired_locked, suspended, etc.
 *   - hasAgency: Whether user belongs to an agency
 *   - agencyId: Agency UUID (if applicable)
 *   - agencyName: Agency name (if applicable)
 *   - agencyRole: Role within agency (OWNER, ADMIN, AGENT, VIEWER)
 *   - isTrialUser: Boolean for trial users
 *   - isTrialActive: Boolean for active trial
 *   - isTrialExpired: Boolean for expired trial
 *   - mfaEnabled: Whether MFA is enabled
 *   - showOnLeaderboard: Leaderboard visibility preference
 *
 * @see https://help.productfruits.com/en/article/developers-integration-react-nextjs
 */
export function ProductFruitsProvider({ children }: ProductFruitsProviderProps) {
  const { user, isInitializing } = useUserContext();

  // Get workspace code from environment
  const workspaceCode = process.env.NEXT_PUBLIC_PRODUCT_FRUITS_WORKSPACE_CODE;

  // Build user info object for ProductFruits
  // useMemo prevents unnecessary re-renders when user object hasn't actually changed
  const userInfo = useMemo(() => {
    if (!user?.user) return null;

    // Parse name into first/last name for ProductFruits
    const nameParts = (user.user.name || "").trim().split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

    return {
      // Required: Unique user identifier
      username: user.user.id,

      // Standard user fields
      email: user.user.email || "",
      firstname: firstName || undefined,
      lastname: lastName || undefined,
      role: user.user.userType || "agent",
      signUpAt: user.user.createdAt || undefined,

      // Custom properties for segmentation and targeting
      // These allow you to create segments in ProductFruits like:
      // "Show tour only to trial users" or "Show feature to agency owners"
      props: {
        // User type & account info
        userType: user.user.userType || "agent",
        accountType: user.user.accountType || "basic",
        accountStatus: user.user.accountStatus || "active",

        // Agency information
        hasAgency: !!user.currentAgency,
        agencyId: user.currentAgency?.agency?.id || "",
        agencyName: user.currentAgency?.agency?.name || "",
        agencyRole: user.currentAgency?.role || "",
        isAgencyOwner: user.user.userType === "agency_owner",
        isAgencyAdmin: user.currentAgency?.role === "ADMIN",

        // Trial information (for onboarding tours)
        isTrialUser: user.user.isTrialUser || false,
        isTrialActive: user.user.isTrialActive || user.isTrialActive || false,
        isTrialExpired: user.user.isTrialExpired || false,
        trialStatus: user.user.trialStatus || "not_trial",

        // Feature flags / settings
        mfaEnabled: user.user.mfaEnabled || false,
        showOnLeaderboard: user.user.showOnLeaderboard || false,
        emailVerified: user.user.emailVerified || false,

        // Admin flag
        isSystemAdmin: user.user.isSystemAdmin || false,

        // Engagement indicators (useful for targeting inactive users)
        isOnline: user.user.isOnline || false,
      },
    };
  }, [
    user?.user?.id,
    user?.user?.email,
    user?.user?.name,
    user?.user?.userType,
    user?.user?.accountType,
    user?.user?.accountStatus,
    user?.user?.createdAt,
    user?.user?.isTrialUser,
    user?.user?.isTrialActive,
    user?.user?.isTrialExpired,
    user?.user?.trialStatus,
    user?.user?.mfaEnabled,
    user?.user?.showOnLeaderboard,
    user?.user?.emailVerified,
    user?.user?.isSystemAdmin,
    user?.user?.isOnline,
    user?.isTrialActive,
    user?.currentAgency?.agency?.id,
    user?.currentAgency?.agency?.name,
    user?.currentAgency?.role,
  ]);

  // Don't render if no workspace code configured
  if (!workspaceCode) {
    return <>{children}</>;
  }

  // Don't render ProductFruits until user is loaded
  if (isInitializing || !userInfo) {
    return <>{children}</>;
  }

  return (
    <>
      {typeof window !== "undefined" && (
        <ProductFruits
          workspaceCode={workspaceCode}
          language="en"
          user={userInfo}
          lifeCycle="neverUnmount"
        />
      )}
      {children}
    </>
  );
}

export default ProductFruitsProvider;
